import { Editor, Point, Range, Text } from 'slate';
import { isKeyHotkey } from 'is-hotkey';
import type AhaEditor from '../../AhaEditor';
import {
  type EditorPluginFactory,
  type MarkdownRevertOptions,
  type OperationFilter,
} from '../../types/aha-editor';
import BlockUtils from '../Blocks/BlockUtils';
import {
  convertElement,
  convertMark,
  getTextUpToCursor,
  revertElement,
  revertMark,
} from './MarkdownUtils';

const isBackspace = isKeyHotkey('backspace');
const isShiftTab = isKeyHotkey('shift+tab');

// Note about regex for markdown plugins:
// - Regex must include the trigger character.
// - For { type: element } the regex must have a capture group
//   for the markdown pattern including the trigger character.
// - For { type: markdown } the regex must have two capture groups - one that
//   includes the text to be marked AND the markdown pattern and one
//   that includes just the text to be marked. Use markdownRegexpForMark()

const MarkdownPlugin: EditorPluginFactory = () => {
  let revertOptions: MarkdownRevertOptions | undefined;

  const handleConvert = (
    event: KeyboardEvent,
    ahaEditor: AhaEditor,
    options: any
  ) => {
    const { editor } = ahaEditor;
    const selection = editor.selection!;

    const { path } = Range.start(selection);

    if (options.test && !options.test(editor)) return false;

    const nodeEntry = Editor.node(editor, path);
    if (!nodeEntry) return false;

    if (!Text.isText(nodeEntry[0])) return false;

    const textString = getTextUpToCursor(editor) || '';

    const regex = options.regex;
    const match = `${textString}${options.triggerCharacter}`.match(regex);
    if (!match) return false;

    const { text, ...activeMarks } = nodeEntry[0];

    const blockEntry = BlockUtils.currentBlock(editor);
    if (!blockEntry) return false;

    const startOfBlock = Point.equals(Editor.start(editor, blockEntry[1]), {
      path: selection.anchor.path,
      offset: Math.max(selection.anchor.offset - match[1].length, 0),
    });
    const endOfBlock = Point.equals(
      Editor.end(editor, blockEntry[1]),
      selection.anchor
    );

    options.match = match;
    options.regex = regex;
    options.activeMarks = activeMarks;
    options.startOfBlock = startOfBlock;
    options.endOfBlock = endOfBlock;
    options.blockEntry = blockEntry;

    if (options.type === 'mark') {
      revertOptions = convertMark(editor, event, path, options);
      return true;
    }

    if (options.type === 'element') {
      revertOptions = convertElement(editor, event, path, options);
      return true;
    }

    return false;
  };

  const convertMarkdown = (event: KeyboardEvent, ahaEditor: AhaEditor) => {
    const { editor, markdown } = ahaEditor;
    const { selection } = editor;
    if (!selection) return false;

    const isCollapsed = Range.isCollapsed(selection);
    if (!isCollapsed) return false;

    for (const markdownOptions of markdown) {
      let result = false;
      const triggerCharacter = markdownOptions.triggerCharacter;
      if (!triggerCharacter) continue;

      if (triggerCharacter.includes(event.key)) {
        result = handleConvert(event, ahaEditor, {
          ...markdownOptions,
          triggerCharacter,
        });
      }

      if (result) return result;
    }

    return false;
  };

  const revertMarkdown = (event: KeyboardEvent, ahaEditor: AhaEditor) => {
    const { editor } = ahaEditor;

    if (!revertOptions) return false;
    if (!isBackspace(event) && !isShiftTab(event)) return false;
    if (isShiftTab(event) && !revertOptions.shiftTabRevert) return false;

    const { type } = revertOptions;
    let result = false;

    if (type === 'mark') {
      result = revertMark(editor, revertOptions);
    }

    if (type === 'element') {
      result = revertElement(editor, revertOptions);
    }

    if (result) event.preventDefault();

    return result;
  };

  const clearRevert: OperationFilter = (editor, op, next) => {
    if (revertOptions) {
      revertOptions = undefined;
    }

    next(editor, op);
  };

  return {
    name: 'Markdown plugin',
    handlers: {
      onKeyDown: [convertMarkdown, revertMarkdown],
    },
    operationFilter: clearRevert,
  };
};

export default MarkdownPlugin;
